"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const specifications = [{
    label: "1064 nm Raman Engine",
    detail: "Fluorescence-free threat ID"
  }, {
    label: "ResQ/CQL Library Integration",
    detail: "13,000+ chemical substances"
  }, {
    label: "MIL-STD-810G & IP68",
    detail: "Ruggedized field deployment"
  }];
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-28">
      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8 xl:gap-12">
          {/* Left Column - 7 cols */}
          <div className="flex flex-col items-start lg:col-span-7">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }}>
              <Badge variant="outline" className="mb-6 inline-flex items-center gap-2 rounded-full border-border bg-secondary/60 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                <span className="size-2 rounded-full bg-primary" />
                Handheld Analytical Platform
              </Badge>
            </motion.div>

            <motion.h1 initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.1,
            ease: "easeOut"
          }} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl">
              CQL Max-ID: Expanded Chemical Threat Identification
            </motion.h1>

            <motion.p initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.2,
            ease: "easeOut"
          }} className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Engineered to complement and expand the capabilities of the Rigaku ResQ and CQL handheld Raman ecosystem. CQL Max-ID delivers rapid, non-contact identification of narcotics, chemical warfare agents, explosives, and precursors through colored packaging and heavy fluorescing matrices.
            </motion.p>

            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.3,
            ease: "easeOut"
          }} className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-md px-6 text-sm font-semibold shadow-sm">
                <Link to="/BookADemoPage" className="inline-flex items-center gap-2">
                  Book a Demonstration
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md border-border bg-background px-6 text-sm font-semibold text-foreground hover:bg-accent">
                <Link to="/DiscussYourApplicationPage">
                  Discuss Application
                </Link>
              </Button>
            </motion.div>

            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.4,
            ease: "easeOut"
          }} className="mt-10 grid w-full grid-cols-1 gap-4 border-t border-border pt-8 sm:grid-cols-3">
              {specifications.map((spec, i) => <div key={spec.label} data-index={i} className="flex flex-col space-y-1">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                    <CheckCircle2 className="size-3.5 text-primary" />
                    <span>{spec.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{spec.detail}</p>
                </div>)}
            </motion.div>
          </div>

          {/* Right Column - 5 cols (Framed Device Photo) */}
          <div className="lg:col-span-5">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2,
            ease: "easeOut"
          }} className="relative mx-auto w-full max-w-lg lg:max-w-none">
              {/* Outer Decorative Glow/Border Frame */}
              <div className="relative rounded-2xl border border-border bg-card p-3 shadow-xl sm:p-4">
                <div className="relative overflow-hidden rounded-xl bg-muted aspect-4/3 sm:aspect-5/4 lg:aspect-4/3">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_max_id_handheld_raman_analyzer.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_max_id_handheld_raman_analyzer.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_max_id_handheld_raman_analyzer.png-wvc-sizes" alt="CQL Max-ID handheld chemical threat identification spectrometer" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" />
                  {/* Subtle contextual overlay badge inside frame */}
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-md border border-border/80 bg-background/90 px-2.5 py-1 text-xs font-medium text-foreground backdrop-blur-xs">
                    <Zap className="size-3.5 text-primary" />
                    <span>1064 nm Laser</span>
                  </div>
                </div>

                {/* Bottom Frame Info Strip */}
                <div className="mt-3 flex items-center justify-between px-1 py-1 sm:mt-4">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="size-4 text-primary" />
                    <span className="text-xs font-medium text-foreground">Antech Validated &amp; Supported</span>
                  </div>
                  <span className="text-[11px] font-mono text-muted-foreground">ISO / GLP Compliant</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}